import type { FC } from 'react'

import Badge from '@/components/atoms/Badge'
import ConfigPreview from '@/components/molecules/ConfigPreview'
import { useScopedI18n } from '@/locales/client'
import type { Hub } from '@/services/schemas'
import { useMobileLayoutStore } from '@/stores/MobileLayoutStore'
import { cn } from '@/util/cn'
import { priceFormatter } from '@/util/formatter'

import { ShowPreviewButton } from '../atoms/ShowPreviewButton'

type HubOverviewProps = {
  hub: Hub
  hidden?: boolean
}

const HubOverview: FC<HubOverviewProps> = ({ hub, hidden }) => {
  const { isMobile, activeMobileTab } = useMobileLayoutStore()
  const t = useScopedI18n('hubOverview')
  const isKit = /kit/i.test(hub.name)

  return (
    <article className={cn(
      'flex flex-col items-center justify-between rounded-t-md bg-rsf-white rounded-b-md border-2 border-rsf-purple-light',
      hidden && 'hidden',
      isMobile && activeMobileTab === 'deluxe' && 'col-span-2',
    )}
    >
      <div className={cn('relative pt-1 laptop-short:pt-0', isMobile && isKit && 'sm:w-60 h-min lg:p-6 lg:py-2 laptop-short:p-2 laptop-short:py-1')}>
        {hub.id && (
          <div className="p-1 sm:p-1 lg:p-4 laptop-short:p-0.5" id={`config-preview-${hub.id}`}>
            <ConfigPreview className="config-grid-small size-full max-h-[116px] max-w-[94px] lg:max-h-[236px] lg:max-w-[234px] laptop-short:max-h-[180px]" hubId={hub.id} />
          </div>
        )}
      </div>

      <div className="flex h-full flex-col items-center justify-between gap-2 p-2 pt-0 lg:gap-4 laptop-short:gap-0.5 laptop-short:p-1">
        <div className="flex flex-col items-center">
          <h2 className="text-pretty text-center font-inter text-sm font-bold leading-tight text-black sm:text-sm lg:text-lg laptop-short:text-xs">
            {hub.name.replace(/kit/gi, '').replace('Reef', 'Reef ')}
          </h2>
          {isKit && (
            <div className="flex w-full justify-center">
              <Badge variant="kit" className="mt-0.5" style={{ padding: '2px 5px 3px 5px' }}>
                {t('kit')}
              </Badge>
            </div>
          )}
        </div>
        {hub.pricing.retailprice && (
          <span className="text-center text-xs font-bold tabular-nums text-rsf-grey-dark sm:text-sm lg:text-base lg:text-rsf-black laptop-short:text-xs">
            {priceFormatter(hub.pricing.retailprice, hub.pricing.currency)}
          </span>
        )}
      </div>
      <ShowPreviewButton hubId={hub.id} />
    </article>
  )
}

export default HubOverview
